import {
  asProblem,
  FilterLayout,
  ListTable,
  PageHeader,
  problemMessage,
  ResourceFilters,
  StatusTag,
  useFilters,
  useFormat,
  useReferenceOptions,
  type FilterDef,
} from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  listOrders,
  ORDER_STATUSES,
  ordersKey,
  PAYMENT_METHODS,
  STATUS_TAG,
  type OnlineOrderItem,
  type OrderStatus,
  type PaymentMethod,
} from './onlineOrdersApi';
import './onlineOrders.scss';

const FILTERS: readonly FilterDef[] = [
  {
    key: 'status',
    label: 'onlineOrders.columns.status',
    type: 'select',
    options: ORDER_STATUSES.map((s) => ({ value: s, label: `onlineOrders.status.${s}` as const })),
  },
  { key: 'storeId', label: 'onlineOrders.columns.store', type: 'reference', ref: 'org.store' },
  { key: 'salesChannelId', label: 'onlineOrders.columns.channel', type: 'reference', ref: 'config.sales_channel' },
  {
    key: 'paymentMethod',
    label: 'onlineOrders.columns.payment',
    type: 'select',
    options: PAYMENT_METHODS.map((m) => ({ value: m, label: `onlineOrders.payment.${m}` as const })),
  },
  { key: 'received', label: 'onlineOrders.columns.received', type: 'date' },
];

/**
 * Online ordering › Orders (FR-ONL-05, FR-ONL-15): every website and app order HO has, newest first, as every admin list
 * (DESIGN_SYSTEM.md §13): search by order number or the customer's phone, filters (status, store, channel, payment, days), pages of
 * 25/50/100. The phone is masked by the server. A row opens the order. Nothing is edited here: the store answers orders.
 */
export function OnlineOrdersPage() {
  const { t } = useTranslation();
  const { dateTime, money } = useFormat();
  const navigate = useNavigate();
  const stores = useReferenceOptions('org.store');
  const channels = useReferenceOptions('config.sales_channel');
  const [search, setSearch] = useState('');
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const filters = useFilters({ status: '', storeId: '', salesChannelId: '', paymentMethod: '', received: '' }, () =>
    setPaging((p) => ({ ...p, page: 1 })),
  );
  const query = { ...filters.applied, search };
  const orders = useQuery({
    queryKey: ordersKey(query, paging.page, paging.pageSize),
    queryFn: () => listOrders(query, paging.page, paging.pageSize),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000, // follows orders moving on
  });
  const label = (options: typeof stores.data, id: string) => options?.find((o) => o.value === id)?.label ?? '—';

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('onlineOrders.title')} description={t('onlineOrders.intro')} />
      <FilterLayout
        filters={filters}
        fields={
          <ResourceFilters
            filters={FILTERS}
            values={filters.draft}
            onChange={(values) => filters.setDraft({ ...filters.draft, ...values })}
          />
        }
      >
        <ListTable<OnlineOrderItem>
          ariaLabel={t('onlineOrders.title')}
          loading={orders.isLoading}
          error={orders.error ? problemMessage(t, asProblem(orders.error)) : undefined}
          onRetry={() => void orders.refetch()}
          rows={orders.data?.items ?? []}
          filters={filters}
          search={{
            label: t('onlineOrders.search'),
            onChange: (value) => {
              setSearch(value.trim());
              setPaging((p) => ({ ...p, page: 1 }));
            },
          }}
          onRowClick={(o) => void navigate(`/online/orders/${o.id}`)}
          rowLabel={(o) => o.orderNumber ?? o.id}
          empty={filters.count > 0 || search ? undefined : { heading: t('onlineOrders.empty') }}
          pagination={{ ...paging, total: Number(orders.data?.total ?? 0), capped: orders.data?.totalCapped, onChange: setPaging }}
          columns={[
            { key: 'orderNumber', header: t('onlineOrders.columns.number'), render: (o) => o.orderNumber ?? '—' },
            { key: 'receivedAt', header: t('onlineOrders.columns.received'), render: (o) => dateTime(o.receivedAt) },
            { key: 'store', header: t('onlineOrders.columns.store'), render: (o) => label(stores.data, o.storeId) },
            {
              key: 'channel',
              header: t('onlineOrders.columns.channel'),
              render: (o) => label(channels.data, o.salesChannelId),
            },
            {
              key: 'customer',
              header: t('onlineOrders.columns.customer'),
              render: (o) => (
                <span className="pos-online-orders__cell">
                  <span>{o.customerName ?? '—'}</span>
                  {o.maskedPhone && <span className="pos-online-orders__secondary">{o.maskedPhone}</span>}
                </span>
              ),
            },
            {
              key: 'fulfilment',
              header: t('onlineOrders.columns.fulfilment'),
              render: (o) =>
                o.fulfilment ? t(`onlineOrders.fulfilment.${o.fulfilment as 'delivery' | 'pickup' | 'dine_in'}`) : '—',
            },
            {
              key: 'payment',
              header: t('onlineOrders.columns.payment'),
              render: (o) => (o.paymentMethod ? t(`onlineOrders.payment.${o.paymentMethod as PaymentMethod}`) : '—'),
            },
            {
              key: 'total',
              header: t('onlineOrders.columns.total'),
              render: (o) => money(Number(o.total), o.currencyCode),
            },
            {
              key: 'status',
              header: t('onlineOrders.columns.status'),
              render: (o) => (
                <StatusTag status={STATUS_TAG[o.status as OrderStatus] ?? 'pending'}>
                  {t(`onlineOrders.status.${o.status as OrderStatus}`, { defaultValue: o.status })}
                </StatusTag>
              ),
            },
          ]}
        />
      </FilterLayout>
    </section>
  );
}
